/**
 * Instagram FourSixty section — custom prev/next navigation.
 *
 * FourSixty renders its own horizontally-scrollable timeline (.fs-wrapper /
 * .fs-timeline, overflow-x:auto). We hide FourSixty's arrows and drive the
 * NATIVE scroll from our own buttons, so the true last tile is always reachable
 * and sits flush right. Prev/Next disabled state derives from scroll position.
 *
 * Registered through the theme's section framework (slate Sections) so it is
 * initialised on load AND on shopify:section:load (Theme Editor), and torn down
 * via onUnload (observer disconnected, listeners + timers removed) — no leaks.
 *
 * FourSixty injects markup asynchronously and lazy-loads / re-lays-out posts,
 * so a single instantaneous scrollWidth read can be transiently short (looking
 * "at the end" when it isn't). We therefore (a) coalesce every trigger
 * (scroll / mutation / resize) into one rAF pass and (b) re-evaluate over the
 * ~1.5s after each click, so a transient reading can't leave Next stuck.
 */
(function () {
  'use strict';

  function InstagramFourSixty(container) {
    this.container = container;
    this.nav = container.querySelector('.il-fs-nav');
    this.prevBtn = this.nav && this.nav.querySelector('.il-fs-prev');
    this.nextBtn = this.nav && this.nav.querySelector('.il-fs-next');
    if (!this.nav || !this.prevBtn || !this.nextBtn) return;

    this.scroller = null;
    this.scheduled = false;
    this.settleTimers = [];

    var self = this;
    this._onScroll = function () { self.schedule(); };
    this._onResize = function () { self.schedule(); };
    this._onPrev = function () { self.page(-1); };
    this._onNext = function () { self.page(1); };

    this.prevBtn.addEventListener('click', this._onPrev);
    this.nextBtn.addEventListener('click', this._onNext);

    this.observer = new MutationObserver(function () { self.schedule(); });
    this.observer.observe(container, { childList: true, subtree: true });

    window.addEventListener('resize', this._onResize, { passive: true });

    this.schedule();
  }

  InstagramFourSixty.prototype.findScroller = function () {
    var candidates = this.container.querySelectorAll('.fs-timeline, .fs-wrapper');
    for (var i = 0; i < candidates.length; i++) {
      if (candidates[i].scrollWidth - candidates[i].clientWidth > 2) {
        return candidates[i];
      }
    }
    return this.container.querySelector('.fs-timeline') || this.container.querySelector('.fs-wrapper');
  };

  // Recompute the disabled state from the (settled) scroll geometry.
  InstagramFourSixty.prototype.apply = function () {
    this.scheduled = false;
    if (!this.prevBtn || !this.nextBtn) return;

    var found = this.findScroller();
    if (found && found !== this.scroller) {
      if (this.scroller) {
        this.scroller.removeEventListener('scroll', this._onScroll);
      }
      this.scroller = found;
      this.scroller.addEventListener('scroll', this._onScroll, { passive: true });
    }

    if (!this.scroller) {
      this.prevBtn.disabled = true;
      this.nextBtn.disabled = true;
      return;
    }

    var max = this.scroller.scrollWidth - this.scroller.clientWidth;
    if (max < 0) { max = 0; }
    var raw = this.scroller.scrollLeft;
    var x = raw < 0 ? -raw : raw;   // normalise negative scrollLeft (RTL / iOS overscroll)
    if (x > max) { x = max; }       // clamp iOS momentum overshoot

    this.prevBtn.disabled = (x <= 1);
    this.nextBtn.disabled = (max <= 1 || x >= max - 1);
  };

  // Coalesce scroll / mutation / resize into one per-frame recompute.
  InstagramFourSixty.prototype.schedule = function () {
    if (this.scheduled) return;
    this.scheduled = true;
    var self = this;
    if (window.requestAnimationFrame) {
      window.requestAnimationFrame(function () { self.apply(); });
    } else {
      window.setTimeout(function () { self.apply(); }, 16);
    }
  };

  // After a click the smooth scroll animates and FourSixty may lazy-load /
  // relayout; re-check across ~1.5s so a transient "at end" reading can't leave
  // a button stuck disabled while more tiles exist.
  InstagramFourSixty.prototype.recheckSettle = function () {
    var self = this, i;
    for (i = 0; i < this.settleTimers.length; i++) {
      window.clearTimeout(this.settleTimers[i]);
    }
    this.settleTimers = [120, 400, 800, 1500].map(function (t) {
      return window.setTimeout(function () { self.apply(); }, t);
    });
  };

  InstagramFourSixty.prototype.page = function (direction) {
    if (!this.scroller) { this.apply(); }
    if (!this.scroller) return;

    var rtl = false;
    try { rtl = window.getComputedStyle(this.scroller).direction === 'rtl'; } catch (e) {}
    var delta = (rtl ? -direction : direction) * this.scroller.clientWidth;

    try {
      this.scroller.scrollBy({ left: delta, behavior: 'smooth' });
    } catch (e) {
      this.scroller.scrollLeft += delta; // older browsers w/o options object
    }
    this.recheckSettle();
  };

  // Called by the slate Sections framework on shopify:section:unload.
  InstagramFourSixty.prototype.onUnload = function () {
    if (this.observer) { this.observer.disconnect(); }
    if (this.scroller) { this.scroller.removeEventListener('scroll', this._onScroll); }
    if (this._onResize) { window.removeEventListener('resize', this._onResize); }
    for (var i = 0; i < this.settleTimers.length; i++) {
      window.clearTimeout(this.settleTimers[i]);
    }
  };

  // Prefer the theme's section framework (init on load + shopify:section:load,
  // teardown via onUnload). Fall back to a direct init if it isn't present.
  if (window.theme && window.theme.sections && typeof window.theme.sections.register === 'function') {
    window.theme._registeredSections = window.theme._registeredSections || {};
    if (!window.theme._registeredSections['instagram-foursixty']) {
      window.theme._registeredSections['instagram-foursixty'] = true;
      window.theme.InstagramFourSixty = InstagramFourSixty;
      window.theme.sections.register('instagram-foursixty', InstagramFourSixty);
    }
  } else {
    var initContainer = function (c) {
      if (c && !c.__ilFsInit) { c.__ilFsInit = true; new InstagramFourSixty(c); }
    };
    var initAll = function () {
      var list = document.querySelectorAll('.instagram--foursixty');
      for (var i = 0; i < list.length; i++) { initContainer(list[i]); }
    };
    if (document.readyState !== 'loading') {
      initAll();
    } else {
      document.addEventListener('DOMContentLoaded', initAll);
    }
    document.addEventListener('shopify:section:load', function (e) {
      initContainer((e.target || document).querySelector('.instagram--foursixty'));
    });
  }
})();
